import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import {
  CheckCircle2, XCircle, Clock, Plus, Trash2, Settings, ArrowRight, User,
} from 'lucide-react';
import {
  useWorkflowEtapas, useWorkflowAprovacoes, useWorkflowMutations,
  WorkflowEtapa, WorkflowAprovacao, getPerfilLabel, PERFIL_OPTIONS,
} from '@/hooks/useEmpWorkflow';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// ============ Workflow Config (Admin) ============
interface WorkflowConfigProps {
  contratoId: string;
  canEdit: boolean;
}

export const WorkflowConfig: React.FC<WorkflowConfigProps> = ({ contratoId, canEdit }) => {
  const { data: etapas = [] } = useWorkflowEtapas(contratoId);
  const mutations = useWorkflowMutations(contratoId);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ nome: '', perfil_aprovador: '', prazo_dias: '5' });

  const handleAdd = () => {
    if (!form.nome.trim() || !form.perfil_aprovador) return;
    const nextOrdem = etapas.length > 0 ? Math.max(...etapas.map(e => e.ordem)) + 1 : 1;
    mutations.upsertEtapa.mutate({
      ordem: nextOrdem,
      nome: form.nome.trim(),
      perfil_aprovador: form.perfil_aprovador,
      prazo_dias: parseInt(form.prazo_dias) || 5,
    }, {
      onSuccess: () => {
        setForm({ nome: '', perfil_aprovador: '', prazo_dias: '5' });
        setAddOpen(false);
      },
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Settings className="h-4 w-4" /> Workflow de Aprovação
        </CardTitle>
        {canEdit && (
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4 mr-1" /> Etapa
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {etapas.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            Nenhuma etapa configurada. Configure o workflow para habilitar aprovação multi-nível.
          </p>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            {etapas.map((e, i) => (
              <React.Fragment key={e.id}>
                <div className="border rounded-lg p-3 min-w-[150px] relative group">
                  <p className="text-xs text-muted-foreground">Etapa {e.ordem}</p>
                  <p className="font-medium text-sm">{e.nome}</p>
                  <Badge variant="outline" className="text-xs mt-1">
                    <User className="h-3 w-3 mr-1" /> {getPerfilLabel(e.perfil_aprovador)}
                  </Badge>
                  <p className="text-xs text-muted-foreground mt-1">{e.prazo_dias}d prazo</p>
                  {canEdit && (
                    <Button
                      variant="ghost" size="icon"
                      className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100"
                      onClick={() => mutations.removeEtapa.mutate(e.id)}
                    >
                      <Trash2 className="h-3 w-3 text-destructive" />
                    </Button>
                  )}
                </div>
                {i < etapas.length - 1 && <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
              </React.Fragment>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Etapa de Aprovação</DialogTitle>
            <DialogDescription>Defina o perfil aprovador e o prazo para esta etapa.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome da Etapa *</Label>
              <Input value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} placeholder="Ex: Aprovação Supervisor" />
            </div>
            <div>
              <Label>Perfil Aprovador *</Label>
              <Select value={form.perfil_aprovador} onValueChange={v => setForm(p => ({ ...p, perfil_aprovador: v }))}>
                <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {PERFIL_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Prazo (dias)</Label>
              <Input type="number" min="1" value={form.prazo_dias} onChange={e => setForm(p => ({ ...p, prazo_dias: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancelar</Button>
            <Button onClick={handleAdd} disabled={!form.nome.trim() || !form.perfil_aprovador || mutations.upsertEtapa.isPending}>
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ============ Workflow Timeline ============
interface WorkflowTimelineProps {
  entidadeTipo: string;
  entidadeId: string;
  contratoId: string;
  canApprove?: boolean;
}

const statusConfig: Record<string, { icon: React.ElementType; color: string; label: string; variant: 'default' | 'destructive' | 'outline' | 'secondary' }> = {
  pendente: { icon: Clock, color: 'text-muted-foreground', label: 'Pendente', variant: 'outline' },
  aprovado: { icon: CheckCircle2, color: 'text-green-600', label: 'Aprovado', variant: 'default' },
  rejeitado: { icon: XCircle, color: 'text-destructive', label: 'Rejeitado', variant: 'destructive' },
  pulado: { icon: ArrowRight, color: 'text-muted-foreground', label: 'Pulado', variant: 'secondary' },
};

export const WorkflowTimeline: React.FC<WorkflowTimelineProps> = ({ entidadeTipo, entidadeId, contratoId, canApprove }) => {
  const { data: aprovacoes = [] } = useWorkflowAprovacoes(entidadeTipo, entidadeId);
  const mutations = useWorkflowMutations(contratoId);
  const { role } = useAuth();

  const [decisionDialog, setDecisionDialog] = useState<WorkflowAprovacao | null>(null);
  const [decision, setDecision] = useState<'aprovado' | 'rejeitado'>('aprovado');
  const [obs, setObs] = useState('');

  if (aprovacoes.length === 0) return null;

  // Find next pending step
  const nextPending = aprovacoes.find(a => a.status === 'pendente');
  const isAdmin = role === 'admin' || role === 'coadmin';

  const handleDecision = () => {
    if (!decisionDialog) return;
    mutations.decidirAprovacao.mutate({
      aprovacaoId: decisionDialog.id,
      status: decision,
      observacoes: obs.trim() || undefined,
      entidadeTipo,
      entidadeId,
    }, {
      onSuccess: () => {
        setDecisionDialog(null);
        setObs('');
      },
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium">Workflow de Aprovação</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {aprovacoes.map((ap, i) => {
            const cfg = statusConfig[ap.status] || statusConfig.pendente;
            const Icon = cfg.icon;
            const isNext = nextPending?.id === ap.id;

            return (
              <div key={ap.id} className={`flex items-start gap-3 ${isNext ? 'bg-primary/5 -mx-3 px-3 py-2 rounded-lg' : ''}`}>
                <div className="flex flex-col items-center">
                  <div className={`rounded-full p-1.5 ${ap.status === 'aprovado' ? 'bg-green-100' : ap.status === 'rejeitado' ? 'bg-destructive/10' : 'bg-muted'}`}>
                    <Icon className={`h-4 w-4 ${cfg.color}`} />
                  </div>
                  {i < aprovacoes.length - 1 && <div className="w-px h-6 bg-border mt-1" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{ap.etapa_nome}</span>
                    <Badge variant={cfg.variant} className="text-xs">{cfg.label}</Badge>
                  </div>
                  {ap.aprovado_em && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {ap.aprovador_nome && <span className="font-medium text-foreground">{ap.aprovador_nome}</span>}
                      {ap.aprovador_nome && ' — '}
                      {format(new Date(ap.aprovado_em), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                      {ap.aprovador_email && <span className="ml-1 text-xs opacity-60">({ap.aprovador_email})</span>}
                    </p>
                  )}
                  {ap.observacoes && (
                    <p className="text-xs text-muted-foreground mt-1 italic">"{ap.observacoes}"</p>
                  )}
                  {isNext && canApprove && isAdmin && (
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" variant="default" onClick={() => { setDecisionDialog(ap); setDecision('aprovado'); setObs(''); }}>
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Aprovar
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => { setDecisionDialog(ap); setDecision('rejeitado'); setObs(''); }}>
                        <XCircle className="h-3 w-3 mr-1" /> Rejeitar
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>

      <Dialog open={!!decisionDialog} onOpenChange={() => setDecisionDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{decision === 'aprovado' ? 'Aprovar' : 'Rejeitar'} — {decisionDialog?.etapa_nome}</DialogTitle>
          </DialogHeader>
          <div>
            <Label>Observações {decision === 'rejeitado' ? '*' : '(opcional)'}</Label>
            <Textarea value={obs} onChange={e => setObs(e.target.value)} placeholder="Registre sua decisão..." rows={3} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionDialog(null)}>Cancelar</Button>
            <Button
              variant={decision === 'rejeitado' ? 'destructive' : 'default'}
              disabled={(decision === 'rejeitado' && !obs.trim()) || mutations.decidirAprovacao.isPending}
              onClick={handleDecision}
            >
              Confirmar {decision === 'aprovado' ? 'Aprovação' : 'Rejeição'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
